import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { PointCloudObject } from "@/object-cache";
import {
  PointCloudAnalysis,
  ReferencePlaneType,
  VolumeAnalysisBasePlaneType,
} from "@/store/point-cloud-analysis-tool-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  computeVolumeAnalysis,
  computeVolumeMesh,
} from "@/utils/colormap-analysis-utils";
import { VolumeMeshGeometry } from "@faro-lotv/lotv";
import { useEffect, useRef, useState, type JSX } from "react";
import { Plane } from "three";
import { ColormapAnalysisRenderer } from "./colormap-analysis-renderer";
import { ReferencePlaneRenderer } from "./reference-plane-renderer";
import { VolumeAnalysisRenderer } from "./volume-analysis-renderer";

/**
 * Delay before recomputing a volume after the clipping planes changed, so the
 * computation runs once after the clipping box edit settles instead of once per frame
 */
const VOLUME_RECOMPUTE_DEBOUNCE_MS = 300;

type AnalysisRendererProps = {
  /** Point cloud object used by analysis */
  pointCloud: PointCloudObject;

  /** The point cloud analysis to be rendered */
  analysis: PointCloudAnalysis;

  /**
   * Optional scale factor for the labels.
   * For example, a labelsScale of 0.2 will render labels at 20% of their default size.
   */
  labelsScale?: number;

  /** The clipping planes currently applied to the point cloud */
  clippingPlanes?: Plane[];
};

/** @returns The renderer of an analysis object */
export function AnalysisRenderer({
  pointCloud,
  analysis,
  labelsScale,
  clippingPlanes,
}: AnalysisRendererProps): JSX.Element | null {
  const [volumeGeometry, setVolumeGeometry] = useState<VolumeMeshGeometry>();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();

  // The clipping planes the current volume result was computed with; comparing
  // against them keeps the recomputation from re-triggering itself when its
  // dispatched result changes the analysis object
  const computedClippingPlanes = useRef(clippingPlanes);

  // Recompute the volume when the clipping planes change
  useEffect(() => {
    if (computedClippingPlanes.current === clippingPlanes) return;
    computedClippingPlanes.current = clippingPlanes;
    if (
      analysis.referencePlaneType !== ReferencePlaneType.volume ||
      !analysis.volumeAnalysisResult
    ) {
      return;
    }
    const timeout = setTimeout(() => {
      // Resetting the geometry re-triggers the mesh computation below
      setVolumeGeometry(undefined);
      computeVolumeAnalysis(
        pointCloud,
        analysis,
        analysis.volumeBasePlaneType ??
          VolumeAnalysisBasePlaneType.bestFitPolygonBase,
        dispatch,
        clippingPlanes,
      ).catch((error) => {
        handleErrorWithToast({
          title: "Could not compute the volume analysis",
          error,
        });
      });
    }, VOLUME_RECOMPUTE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [analysis, clippingPlanes, dispatch, handleErrorWithToast, pointCloud]);

  useEffect(() => {
    let cancelled = false;
    async function updateVolumeGeometry(): Promise<void> {
      const geometry = await computeVolumeMesh(
        pointCloud,
        analysis,
        clippingPlanes,
      );
      if (!cancelled) {
        setVolumeGeometry(geometry);
      }
    }
    if (
      analysis.referencePlaneType === ReferencePlaneType.volume &&
      analysis.volumeAnalysisResult &&
      !volumeGeometry
    ) {
      updateVolumeGeometry();
      return () => {
        cancelled = true;
      };
    }
  }, [
    analysis,
    analysis.referencePlaneType,
    analysis.volumeAnalysisResult,
    clippingPlanes,
    pointCloud,
    volumeGeometry,
  ]);

  return (
    <>
      {analysis.showReferencePlane && (
        <ReferencePlaneRenderer analysis={analysis} />
      )}
      {analysis.referencePlaneType !== ReferencePlaneType.volume && (
        <ColormapAnalysisRenderer
          pointCloud={pointCloud}
          analysis={analysis}
          labelsScale={labelsScale}
        />
      )}
      {analysis.referencePlaneType === ReferencePlaneType.volume &&
        volumeGeometry && (
          <VolumeAnalysisRenderer
            analysis={analysis}
            volumeGeometry={volumeGeometry}
          />
        )}
    </>
  );
}
